import { useState, useRef, useEffect, useCallback } from "react";
import api from "../lib/api";
import { saveLocalSession } from "../lib/local";

const STATE_STYLES = {
  idle: { bg: "#0e0e0e", label: "READY?", sub: "Click, tap, or press Space when the panel turns green." },
  waiting: { bg: "#DC2626", label: "WAIT...", sub: "Don't move. Wait for green." },
  go: { bg: "#39FF14", label: "REACT!", sub: "", dark: true },
  nogo: { bg: "#F59E0B", label: "DON'T REACT", sub: "Ignore this one.", dark: true },
  early: { bg: "#3B82F6", label: "TOO EARLY!", sub: "Wait for the signal. Anticipating doesn't count." },
  round: { bg: "#0e0e0e", label: "", sub: "" },
};

function detectDevice() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.stop(ctx.currentTime + 0.26);
  } catch {}
}

export default function ReactionTest({
  testType = "classic",
  rounds = 5,
  isDaily = false,
  challengeCode = null,
  inputMethod = "mouse",
  onFinished,
}) {
  const [phase, setPhase] = useState("idle");
  const [attempts, setAttempts] = useState([]);
  const [falseStarts, setFalseStarts] = useState(0);
  const [lastMs, setLastMs] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const startTimeRef = useRef(0);
  const timerRef = useRef(null);
  const nogoTimerRef = useRef(null);
  const lockRef = useRef(false);
  const attemptsRef = useRef([]);
  const falseRef = useRef(0);
  const phaseRef = useRef("idle");
  const inputRef = useRef(inputMethod);

  const validCount = attempts.filter((a) => !a.false_start).length;

  const setPhaseSafe = (p) => {
    phaseRef.current = p;
    setPhase(p);
  };

  const clearTimers = () => {
    clearTimeout(timerRef.current);
    clearTimeout(nogoTimerRef.current);
  };

  const beginRound = useCallback(() => {
    lockRef.current = false;
    setPhaseSafe("waiting");
    const isNoGo = testType === "gonogo" && Math.random() < 0.28;
    const delay = 1200 + Math.random() * 2600;
    timerRef.current = setTimeout(() => {
      startTimeRef.current = performance.now();
      if (testType === "audio") beep();
      setPhaseSafe(isNoGo ? "nogo" : "go");
      if (isNoGo) {
        // auto-advance if user correctly does NOT react
        nogoTimerRef.current = setTimeout(() => {
          if (phaseRef.current === "nogo") beginRound();
        }, 1100);
      }
    }, delay);
  }, [testType]);

  const finish = useCallback(async () => {
    setSubmitting(true);
    const payload = {
      test_type: testType,
      attempts: attemptsRef.current,
      false_starts: falseRef.current,
      device: detectDevice(),
      input_method: inputRef.current,
      is_daily: isDaily,
      challenge_code: challengeCode,
    };
    try {
      const { data } = await api.post("/test/session", payload);
      saveLocalSession(data);
      onFinished(data);
    } catch (e) {
      onFinished(null, e);
    } finally {
      setSubmitting(false);
    }
  }, [testType, isDaily, challengeCode, onFinished]);

  const registerReaction = useCallback((ms) => {
    const next = [...attemptsRef.current, { reaction_ms: Math.round(ms), false_start: false }];
    attemptsRef.current = next;
    setAttempts(next);
    setLastMs(Math.round(ms));
    const valid = next.filter((a) => !a.false_start).length;
    if (valid >= rounds) {
      setPhaseSafe("done");
      setTimeout(() => finish(), 400);
    } else {
      setPhaseSafe("round");
      setTimeout(() => beginRound(), 750);
    }
  }, [rounds, beginRound, finish]);

  const registerFalseStart = useCallback((message) => {
    const next = [...attemptsRef.current, { reaction_ms: 0, false_start: true }];
    attemptsRef.current = next;
    falseRef.current += 1;
    setAttempts(next);
    setFalseStarts(falseRef.current);
    setPhaseSafe("early");
    setTimeout(() => beginRound(), 1200);
  }, [beginRound]);

  const handleReact = useCallback(() => {
    const p = phaseRef.current;
    if (lockRef.current) return;
    if (p === "waiting") {
      lockRef.current = true;
      clearTimers();
      registerFalseStart();
    } else if (p === "go") {
      lockRef.current = true;
      const ms = performance.now() - startTimeRef.current;
      if (ms < 40) return;
      registerReaction(ms);
    } else if (p === "nogo") {
      lockRef.current = true;
      clearTimers();
      registerFalseStart();
    }
  }, [registerReaction, registerFalseStart]);

  const start = () => {
    attemptsRef.current = [];
    falseRef.current = 0;
    setAttempts([]);
    setFalseStarts(0);
    setLastMs(null);
    beginRound();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.code === "Enter" || e.key === " ") {
        e.preventDefault();
        if (phaseRef.current === "idle") { inputRef.current = "keyboard"; start(); }
        else handleReact();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); clearTimers(); };
    // eslint-disable-next-line
  }, [handleReact]);

  const active = phase === "waiting" || phase === "go" || phase === "nogo" || phase === "early";
  const style = STATE_STYLES[phase] || STATE_STYLES.idle;

  const onPointer = (e) => {
    if (phase === "idle" || phase === "done" || phase === "round") return;
    inputRef.current = e.pointerType === "touch" ? "touch" : "mouse";
    handleReact();
  };

  return (
    <div
      data-testid="reaction-test-panel"
      onPointerDown={onPointer}
      className="relative w-full h-full min-h-[420px] rounded-3xl overflow-hidden flex flex-col items-center justify-center text-center no-select touch-none cursor-pointer transition-colors duration-100"
      style={{ backgroundColor: active ? style.bg : "#0e0e0e", border: active ? "none" : "1px solid rgba(255,255,255,0.07)" }}
      aria-live="assertive"
    >
      {/* round counter */}
      {(phase !== "idle") && (
        <div className="absolute top-5 left-0 right-0 flex justify-center">
          <span data-testid="round-counter" className={`font-mono text-sm tracking-widest ${style.dark ? "text-black/70" : "text-white/70"}`}>
            ROUND {Math.min(validCount + 1, rounds)} / {rounds}
          </span>
        </div>
      )}

      {phase === "idle" && (
        <div className="px-6">
          <div className="text-3xl sm:text-5xl font-display font-black tracking-tight mb-4">READY?</div>
          <p className="text-white/60 max-w-md mx-auto mb-8 text-sm sm:text-base">{STATE_STYLES.idle.sub}</p>
          <button
            data-testid="start-test-button"
            onClick={(e) => { e.stopPropagation(); start(); }}
            className="btn-glow bg-[#39FF14] text-black font-display font-extrabold text-lg px-12 py-4 rounded-full hover:bg-[#32e612]"
          >
            START
          </button>
        </div>
      )}

      {(phase === "waiting" || phase === "go" || phase === "nogo" || phase === "early") && (
        <div className="px-6 pointer-events-none">
          <div className={`font-display font-black tracking-tight ${style.dark ? "text-black" : "text-white"} text-[12vw] sm:text-6xl leading-none`}>
            {style.label}
          </div>
          {style.sub && <p className={`mt-4 text-sm sm:text-base ${style.dark ? "text-black/70" : "text-white/80"}`}>{style.sub}</p>}
          {phase === "go" && <p className="sr-only">GREEN SIGNAL — REACT NOW</p>}
        </div>
      )}

      {phase === "round" && (
        <div className="px-6">
          <div data-testid="round-result" className="font-mono font-extrabold text-6xl sm:text-7xl text-[#39FF14] glow-green">{lastMs}<span className="text-2xl text-white/40 ml-2">ms</span></div>
          <p className="text-white/50 mt-3 text-sm">Get ready for the next one…</p>
        </div>
      )}

      {(phase === "done") && (
        <div className="px-6">
          <div className="font-display font-black text-3xl mb-2">{submitting ? "Crunching your numbers…" : "Done!"}</div>
          <div className="w-10 h-10 border-2 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto mt-4" />
        </div>
      )}

      {falseStarts > 0 && phase !== "idle" && (
        <div className="absolute bottom-5 left-0 right-0 text-center">
          <span data-testid="false-start-count" className={`font-mono text-xs ${style.dark ? "text-black/60" : "text-white/50"}`}>False starts: {falseStarts}</span>
        </div>
      )}
    </div>
  );
}
